import {uploadUtils} from 'front-onceforall-core';
import PropTypes from 'prop-types';
import React, {useReducer} from 'react';
import {connect} from 'react-redux';
import {Translate} from 'react-redux-i18n';
import {bindActionCreators} from 'redux';

import * as snackbarsActions from '../../../actions/snackbars';
import * as notificationsActions from '../../../actions/notifications';
import notificationService from '../../../services/notification/NotificationService';
import {captureError} from '../../../utils/log';

import UploadFileView from './UploadFileView';

const SUPPORTED_EXTENSIONS = '.pdf';
const INITIAL_STATE = Object.freeze({submittable: false});

const stateReducer = (state, action) => {
  const {error, filename, file} = action;
  switch (action.type) {
    case 'reset':
      return {...INITIAL_STATE};
    case 'rejected':
      return {submittable: false, error};
    case 'accepted':
      return {submittable: true, filename, file};
    default: {
      const typeError = new TypeError(`Unknown action ${action.type}`);
      captureError('Unknow upload file action', typeError);
      throw typeError;
    }
  }
};

export const UploadFileContainer = props => {
  const [submitted, setSubmitted] = React.useState(false);
  const [fileState, setFileState] = useReducer(stateReducer, {...props.initialFileState});

  const refreshNotificationsAndClose = () => {
    // Not displaying an error on refresh error to avoid confusing the user about the success of his upload
    props.actions.fetchNotifications(props.organisation)
      .catch(error => captureError('Error while refreshing notifications', error));
    props.onClose();
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const {submittable, file} = fileState;
    if (submittable && file) {
      setSubmitted(true);
      try {
        // updateNotificationByUploadingFile also handles dismissal
        await notificationService.updateNotificationByUploadingFile(props.notificationId, file);
      } catch (error) {
        setSubmitted(false);
        captureError('Error while uploading file', error);
        props.actions.addError(<Translate value="error.generic"/>);
        // Avoid closing the panel
        return;
      }

      refreshNotificationsAndClose();
    }
  };

  const handleRejectedFiles = rejectedFile => {
    switch (rejectedFile.errors[0].code) {
      case 'file-too-large':
        setFileState(
          {
            error: (<Translate
              value="document.errorMaxSizeMessage"
              count={(uploadUtils.DOCUMENT_MAX_SIZE / 1000000).toString()}
            />),
            type: 'rejected'
          }
        );
        break;
      case 'file-too-small':
        setFileState(
          {
            error: (<Translate
              value="document.errorMinSizeMessage"
              count={(uploadUtils.DOCUMENT_MIN_SIZE / 1000).toString()}
            />),
            type: 'rejected'
          }
        );
        break;
      case 'file-invalid-type':
        setFileState(
          {
            error: (<><Translate value="document.errorTypeMessage"/>{SUPPORTED_EXTENSIONS.replace(/,/g, ' | ')}</>),
            type: 'rejected'
          }
        );
        break;
      default:
        setFileState(
          {
            error: (<Translate value="error.generic"/>),
            type: 'rejected'
          }
        );
    }
  };

  const handleFileSelection = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles?.length > 0) {
      handleRejectedFiles(rejectedFiles[0]);
    } else if (acceptedFiles?.length > 0) {
      setFileState({filename: acceptedFiles[0].name, file: acceptedFiles[0], type: 'accepted'});
    }
  };

  return (<UploadFileView
    documentName={props.documentName}
    filename={fileState.filename}
    extensions={SUPPORTED_EXTENSIONS}
    fileMaxSize={uploadUtils.DOCUMENT_MAX_SIZE}
    fileMinSize={uploadUtils.DOCUMENT_MIN_SIZE}
    errorMessage={fileState.error}
    submittable={fileState.submittable}
    submitted={submitted}
    onSubmit={handleSubmit}
    onFileSelection={handleFileSelection}
    onClose={props.onClose}
  />);
};

UploadFileContainer.defaultProps = {
  initialFileState: INITIAL_STATE
};

UploadFileContainer.propTypes = {
  initialFileState: PropTypes.object,
  actions: PropTypes.object.isRequired,
  organisation: PropTypes.object.isRequired,
  notificationId: PropTypes.number.isRequired,
  documentName: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({...snackbarsActions, ...notificationsActions}, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UploadFileContainer);
